<template>
    <vrm-scroll-fade :class="hasMounted ? null : 'before-mounted'">
        <div ref="container" class="vrm-table--installations" :style="tableStyle">
            <div
                class="vrm-table--installations__row vrm-table--installations__header"
                :style="headerStyle"
            >
                <div
                    v-for="column in header"
                    :id="column.key"
                    :key="column.key"
                    class="box box--th"
                    :class="[sortIcon(column.key), column.headerClasses]"
                    :style="headerCellStyle"
                    @click="toggleSort(column.key)"
                >
                    <vrm-installations-table-dropdown
                        v-if="column.key === 'battery' && batterySortDropdownOpen"
                        :sort-on="sortOn"
                        :sorts-options="batteryDropdownOptions"
                        :is-sort="isCurrentSort"
                    />
                    <vrm-installations-table-dropdown
                        v-if="column.key === 'fuel' && tankSortDropdownOpen"
                        :sort-on="sortOn"
                        :sorts-options="tankDropdownOptions"
                        :is-sort="isCurrentSort"
                    />
                    <vrm-installations-table-dropdown
                        v-if="column.key === 'temperature' && temperatureSortDropdownOpen"
                        :sort-on="sortOn"
                        :sorts-options="temperatureDropdownOptions"
                        :is-sort="isCurrentSort"
                    />
                    {{ $t(column.i18n, { i18nFallback: column.i18nFallback }) }}
                </div>
            </div>
            <div
                v-if="totalEnabled"
                class="vrm-table--installations__row vrm-table--installations__footer"
            >
                <div
                    v-for="(column, index) in header"
                    :key="column.key"
                    class="box box--total"
                    :class="[
                        column.headerClasses,
                        {
                            disabled:
                                (!Object.keys(columnsWithEnergy).includes(column.key) &&
                                    index !== 0) ||
                                column.key === 'alarm',
                        },
                    ]"
                >
                    <div
                        v-if="
                            Object.keys(columnsWithEnergy).includes(column.key) ||
                                column.key === 'alarm'
                        "
                    >
                        {{ column.key === 'alarm' ? alarmTotal() : propTotal(column.key) }}
                    </div>
                    <div v-else-if="index === 0">{{ $t('installationoverview.total') }}</div>
                </div>
            </div>

            <recycle-scroller
                ref="scroller"
                v-slot="{ item }"
                page-mode
                :items="sortedItems"
                :item-size="itemHeight"
                :buffer="bufferSize"
                key-field="id"
            >
                <div v-if="storedId === item.id" class="vrm-overview-tooltip">
                    <div class="vrm-overview-tooltip__content" :style="{ flex: tooltipFlex }">
                        <div class="timestamp_box vrm-overview-tooltip__item">
                            {{ tooltipTimestamp }}
                            <span class="timestamp_hours">{{ tooltipTimestampHours }}</span>
                        </div>
                        <span v-if="columns.battery.visible" class="breaking_line"></span>
                        <div
                            v-if="columns.battery.visible"
                            :class="{
                                'vrm-overview-tooltip__item': true,
                                battery: true,
                                battery_tooltip: tooltipformatBattery !== null,
                                faded: tooltipformatBattery === null,
                            }"
                        >
                            <div class="vrm-overview-tooltip__icon"></div>
                            <span class="tooltip_content truncate">
                                {{
                                    $t(columns.battery.i18n, {
                                        i18nFallback: columns.battery.i18nFallback,
                                    })
                                }}
                            </span>
                            <span class="tooltip_value">
                                {{ tooltipformatBattery !== null ? tooltipformatBattery : '' }}
                            </span>
                        </div>
                        <span v-if="columns.solar.visible" class="breaking_line"></span>
                        <div
                            v-if="columns.solar.visible"
                            :class="{
                                'vrm-overview-tooltip__item': true,
                                solar: true,
                                solar_tooltip: tooltipformatSolar !== null,
                                faded: tooltipformatSolar === null,
                            }"
                        >
                            <div class="vrm-overview-tooltip__icon"></div>
                            <span class="tooltip_content truncate">
                                {{
                                    $t(columns.solar.i18n, {
                                        i18nFallback: columns.solar.i18nFallback,
                                    })
                                }}
                            </span>
                            <span class="tooltip_value">
                                {{ tooltipformatSolar !== null ? tooltipformatSolar : '' }}
                            </span>
                        </div>
                        <span v-if="columns.load.visible" class="breaking_line"></span>
                        <div
                            v-if="columns.load.visible"
                            :class="{
                                'vrm-overview-tooltip__item': true,
                                load: true,
                                load_tooltip: tooltipformatLoad !== null,
                                faded: tooltipformatLoad === null,
                            }"
                        >
                            <div class="vrm-overview-tooltip__icon"></div>
                            <span class="tooltip_content truncate">
                                {{
                                    $t('installationoverview.max_load', {
                                        i18nFallback: 'Max load',
                                    })
                                }}
                            </span>
                            <span class="tooltip_value">
                                {{ tooltipformatLoad !== null ? tooltipformatLoad : '' }}
                            </span>
                        </div>
                        <span v-if="columns.grid.visible" class="breaking_line"></span>
                        <div
                            v-if="columns.grid.visible"
                            :class="{
                                'vrm-overview-tooltip__item': true,
                                grid: true,
                                grid_tooltip: tooltipformatGrid !== null,
                                faded: tooltipformatGrid === null,
                            }"
                        >
                            <div class="vrm-overview-tooltip__icon"></div>
                            <span class="tooltip_content truncate">
                                {{
                                    $t(columns.grid.i18n, {
                                        i18nFallback: columns.grid.i18nFallback,
                                    })
                                }}
                            </span>
                            <span class="tooltip_value">
                                {{ tooltipformatGrid !== null ? tooltipformatGrid : '' }}
                            </span>
                        </div>
                        <span v-if="columns.generator.visible" class="breaking_line"></span>
                        <div
                            v-if="columns.generator.visible"
                            :class="{
                                'vrm-overview-tooltip__item': true,
                                generator: true,
                                generator_tooltip: tooltipformatGenerator !== null,
                                faded: tooltipformatGenerator === null,
                            }"
                        >
                            <div class="vrm-overview-tooltip__icon"></div>
                            <span class="tooltip_content truncate">
                                {{
                                    $t(columns.generator.i18n, {
                                        i18nFallback: columns.generator.i18nFallback,
                                    })
                                }}
                            </span>
                            <span class="tooltip_value">
                                {{ tooltipformatGenerator !== null ? tooltipformatGenerator : '' }}
                            </span>
                        </div>
                    </div>
                    <div
                        class="vrm-overview-tooltip__spacers"
                        :style="{ flex: tooltipSpacerFlex }"
                    ></div>
                    <div v-if="columns.alarm.visible" id="empty_alarm" class="box box--alarm"></div>
                </div>

                <router-link
                    :ref="'row_' + item.id"
                    :to="{ name: 'base.installation.dashboard', params: { id: item.id } }"
                    class="vrm-table--installations__row vrm-table--installations__row--link"
                >
                    <div v-if="columns.name.visible" class="box">
                        <div class="box__content small-text">
                            {{ getInstallationName(item.name) }}
                        </div>
                    </div>
                    <div
                        v-if="columns.battery.visible"
                        class="box"
                        :class="columns.battery.blockState(item)"
                        v-on="
                            getRouterInterceptorsForTouch(
                                item,
                                'bs',
                                columns.battery.blockState(item),
                            )
                        "
                    >
                        <span class="box__top-bar">
                            <span class="extraSmall right-top">
                                {{ propRead(item, 'soc_status', 'textValue') }}
                                <span
                                    :class="{
                                        'sg-vrm-icon_021_battery-16px': propRead(
                                            item,
                                            'soc_status',
                                            'textValue',
                                        ),
                                    }"
                                />
                            </span>
                        </span>
                        <div class="box__content two-values">
                            <vrm-battery-icon-installation-overview
                                v-if="item.data.bs"
                                :battery-percentage="propRead(item, 'bs')"
                            />
                            <span v-if="item.data.bs && item.data.bv">|</span>
                            {{ propRead(item, 'bv') }}
                        </div>
                        <div v-if="detailMode" class="chart">
                            <vrm-installations-table-chart
                                :ref="'chart-bs-' + item.id"
                                attribute="bs"
                                :block-state="columns.battery.blockState(item)"
                                :id-site="item.id"
                                v-on="getMinigraphEventListeners()"
                            />
                        </div>
                    </div>
                    <div
                        v-if="columns.solar.visible"
                        class="box"
                        :class="columns.solar.blockState(item)"
                        v-on="
                            getRouterInterceptorsForTouch(
                                item,
                                'solar_yield',
                                columns.solar.blockState(item),
                            )
                        "
                    >
                        <div class="box__content">
                            {{ propRead(item, 'solar_yield') }}
                        </div>
                        <div v-if="detailMode" class="chart">
                            <vrm-installations-table-chart
                                :ref="'chart-solar_yield-' + item.id"
                                attribute="solar_yield"
                                :block-state="columns.solar.blockState(item)"
                                :id-site="item.id"
                                v-on="getMinigraphEventListeners()"
                            />
                        </div>
                    </div>
                    <div
                        v-if="columns.load.visible"
                        class="box"
                        :class="columns.load.blockState(item)"
                        v-on="
                            getRouterInterceptorsForTouch(
                                item,
                                'consumption',
                                columns.load.blockState(item),
                            )
                        "
                    >
                        <span class="box__top-bar">
                            <span class="extraSmall center-top">
                                {{ columns.load.annotation(item) }}
                            </span>
                        </span>
                        <div class="box__content">
                            {{ propRead(item, 'consumption') }}
                        </div>
                        <div v-if="detailMode" class="chart">
                            <vrm-installations-table-chart
                                :ref="'chart-consumption-' + item.id"
                                attribute="consumption"
                                :block-state="columns.load.blockState(item)"
                                :id-site="item.id"
                                v-on="getMinigraphEventListeners()"
                            />
                        </div>
                    </div>
                    <div
                        v-if="columns.grid.visible"
                        class="box"
                        :class="columns.grid.blockState(item)"
                        v-on="
                            getRouterInterceptorsForTouch(
                                item,
                                'from_to_grid',
                                columns.grid.blockState(item),
                            )
                        "
                    >
                        <span class="box__top-bar">
                            <span class="extraSmall center-top">
                                {{ columns.grid.annotation(item) }}
                            </span>
                        </span>
                        <div class="box__content">
                            {{ propRead(item, 'from_to_grid') }}
                        </div>
                        <div v-if="detailMode" class="chart">
                            <vrm-installations-table-chart
                                :ref="'chart-from_to_grid-' + item.id"
                                attribute="from_to_grid"
                                :block-state="columns.grid.blockState(item)"
                                :id-site="item.id"
                                v-on="getMinigraphEventListeners()"
                            />
                        </div>
                    </div>
                    <div
                        v-if="columns.paygo.visible"
                        class="box"
                        :class="columns.paygo.blockState(item)"
                    >
                        <div class="box__content small-text">
                            <span class="small">
                                {{ columns.paygo.content(item) }}
                            </span>
                        </div>
                    </div>
                    <div
                        v-if="columns.generator.visible"
                        class="box"
                        :class="columns.generator.blockState(item)"
                        v-on="
                            getRouterInterceptorsForTouch(
                                item,
                                'generator',
                                columns.generator.blockState(item),
                            )
                        "
                    >
                        <span class="box__top-bar">
                            <span class="extraSmall right-top">
                                {{ columns.generator.annotation(item) }}
                            </span>
                        </span>

                        <div v-if="columns.generator.hasData(item)" class="box__content centered">
                            <span class="box__content__value nowrap">
                                {{ propRead(item, 'generator') }}
                            </span>
                        </div>

                        <div v-if="detailMode" class="chart">
                            <vrm-installations-table-chart
                                :ref="'chart-generator-' + item.id"
                                attribute="generator"
                                :block-state="columns.generator.blockState(item)"
                                :id-site="item.id"
                                v-on="getMinigraphEventListeners()"
                            />
                        </div>
                    </div>
                    <div v-if="columns.serviceTime.visible" class="box">
                        <span
                            v-if="columns.serviceTime.hasData(item)"
                            class="box__content__value nowrap"
                            :class="{ 'yellow dark-text': columns.serviceTime.hasError(item) }"
                        >
                            {{ secondsStringToHoursNumber(propRead(item, 'gasd')) }} h
                        </span>
                    </div>
                    <div v-if="columns.runningSince.visible" class="box">
                        <span
                            v-if="columns.runningSince.hasData(item)"
                            class="box__content__value nowrap"
                        >
                            {{ columns.runningSince.runtimeStart(item) }}

                            <div class="box__content__value__detail">
                                {{ columns.runningSince.runtimeElapsed(item) }}
                            </div>
                        </span>
                    </div>
                    <div
                        v-if="columns.fuel.visible"
                        v-tooltip="columns.fuel.tooltip(item)"
                        class="box"
                        :class="columns.fuel.blockState(item)"
                    >
                        <keep-alive>
                            <vrm-pagination-list
                                v-if="columns.fuel.hasData(item)"
                                :items="columns.fuel.tanks(item)"
                                :threshold="lowFuelThreshold"
                                :detail-mode="detailMode"
                                item-component="vrm-tank"
                            />
                        </keep-alive>
                    </div>
                    <div
                        v-if="columns.temperature.visible"
                        v-tooltip="columns.temperature.tooltip(item)"
                        class="box"
                        :class="columns.temperature.blockState(item)"
                    >
                        <keep-alive>
                            <vrm-pagination-list
                                v-if="columns.temperature.hasData(item)"
                                :items="columns.temperature.temperature(item)"
                                :detail-mode="detailMode"
                                item-component="vrm-temperature"
                                :items-per="2"
                                divider
                            />
                        </keep-alive>
                    </div>
                    <div v-if="columns.lastUpdate.visible" class="box">
                        <div class="box__content small-text">
                            <span class="small">
                                {{ columns.lastUpdate.content(item) }}
                            </span>
                        </div>
                    </div>
                    <div v-if="columns.dateAdded.visible" class="box">
                        <div class="box__content small-text">
                            {{ columns.dateAdded.content(item) }}
                        </div>
                    </div>
                    <router-link
                        :is="item.alarm ? 'router-link' : 'div'"
                        :to="{ name: 'base.installation.alarmlogs', params: { id: item.id } }"
                        style="text-decoration:none"
                    >
                        <div
                            v-if="columns.alarm.visible"
                            v-tooltip.bottom="columns.alarm.tooltip(item)"
                            class="box box--alarm"
                            :class="columns.alarm.blockState(item)"
                        >
                            <div class="box__content alarms">
                                <span v-if="siteIsOld(item)" class="sg-vrm-icon_101_offline-16px" />

                                <span
                                    v-else-if="item.alarm"
                                    class="vrm-widget__header__icon sg-vrm-icon_012_alarm-32px"
                                />

                                <span
                                    v-else-if="serviceNeeded(item)"
                                    class="vrm-widget__header__icon sg-vrm-icon_124_wrench-16px"
                                />

                                <span
                                    v-else-if="hasLowTanks(item)"
                                    class="vrm-widget__header__icon sg-vrm-icon_096_tank_16px"
                                />
                            </div>
                        </div>
                    </router-link>
                </router-link>
            </recycle-scroller>
        </div>
    </vrm-scroll-fade>
</template>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { RecycleScroller } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import { AngularDirective, ngInject } from '@/vue/decorators/angular-interop';
import VrmScrollFade from '@/vue/components/shared/VrmScrollFade';
import VrmInstallationsTableChart from './VrmInstallationsTableChart';
import VrmInstallationsTableDropdown from '@/vue/components/shared/VrmInstallationsTableDropdown';
import VrmPaginationList from '@/vue/components/shared/VrmPaginationList';
import moment from 'moment';
import '../atoms/v-tooltip';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import { createSorter, chainSorters, createSorterList } from '@/vue/helpers/sorters';
import { secondsStringToHoursNumber } from '@/vue/helpers/handleTime';
import {
    propValid,
    propRead,
    createPropReader,
    createPropReaderStringToNumber,
    siteIsOld,
    stateFromErrorAndData,
    getSafeInstancesProp,
    getTanksSortedByTypeAndPercentage,
    getTemperaturesSortedByTypeAndValue,
} from '@/vue/helpers/handleInstallationsValues';
import { LocalStorageBound } from '@/vue/decorators/localstorage';
import {
    columnOrder,
    columnsWithEnergy,
    batterySortOptions,
    sortOrders,
    batteryDropdownOptions,
    tankSortOptions,
    temperatureSortOptions,
    tankDropdownOptions,
    temperatureDropdownOptions,
    lowFuelThreshold,
} from '@/vue/static/installationsTableConstants';
import { localeMeta } from '../../../languages/meta';
import { debounce } from 'lodash';
import { generatorTooltipText, generatorTooltipFlex } from '../../helpers/tooltipBulletPoint.js';
import Capabilities from '@/utils/capabilities';
import VrmBatteryIconInstallationOverview from '../shared/VrmBatteryIconInstallationOverview.vue';

@Component({
    components: {
        VrmPaginationList,
        VrmInstallationsTableChart,
        VrmInstallationsTableDropdown,
        VrmScrollFade,
        RecycleScroller,
        VrmBatteryIconInstallationOverview,
    },
    emits: ['running-since-loaded'],
})
@AngularDirective('vrm-installations-table-renderer')
@AngularLocaleBundle('installationoverview')
@AngularLocaleBundle('dashboard')
export default class VrmInstallationsTableRenderer extends Vue {
    @Prop()
    detailMode;
    @Prop()
    enabledColumns;
    @Prop()
    search;
    @Prop()
    installations;
    @Prop()
    totalEnabled;

    @ngInject()
    ApiService;

    @ngInject()
    VrmLangService;

    @ngInject()
    AppearanceService;

    @ngInject()
    GraphService;

    hoveredPoint = null;

    columnsWithEnergy = columnsWithEnergy;

    batterySortDropdownOpen = false;

    tankSortDropdownOpen = false;

    temperatureSortDropdownOpen = false;

    batteryDropdownOptions = batteryDropdownOptions;

    tankSortDropdown = false;

    tankDropdownOptions = tankDropdownOptions;

    temperatureDropdownOptions = temperatureDropdownOptions;

    lowFuelThreshold = lowFuelThreshold;

    secondsStringToHoursNumber = secondsStringToHoursNumber;

    siteIsOld = siteIsOld;

    propRead = propRead;

    charts = {};
    highlightedPoints = {};

    tooltipformatBattery = null;

    tooltipformatSolar = null;

    tooltipformatLoad = null;

    tooltipformatGrid = null;

    tooltipformatGenerator = null;

    tooltipTimestamp = null;

    tooltipTimestampHours = null;

    storedId = null;

    @ngInject()
    locale;
    @ngInject()
    $filter;

    hasMounted = false;
    bufferSize = 0;

    @LocalStorageBound('vrm-installation-table-renderer.sortedColumn')
    sortedColumn = 'alarm';
    @LocalStorageBound('vrm-installation-table-renderer.sortedReverse')
    sortedReverse = false;

    sortAttribute = batterySortOptions.SOC;

    getInstallationName(name) {
        return name.length > 50 ? `${name.substring(0, 44)}...` : name;
    }

    getMinigraphEventListeners() {
        let listeners = {
            'graph-ready': e => this.onGraphReady(e),
        };

        if (Capabilities.hasTouchSupport()) {
            listeners['tap'] = e => this.onMinigraphTap(e);
        }

        if (Capabilities.hasPointerSupport()) {
            listeners['mouse-enter'] = e => this.onMouseEnter(e);
            listeners['mouse-move'] = e => this.onMouseMoveThrottled(e);
            listeners['mouse-leave'] = e => this.onMouseLeave(e);
        }

        return listeners;
    }

    getTanksInfo(installation, rawInfo = false) {
        const { tl, tf, tcn } = installation.data;
        if (!tl) return undefined;
        const raw = [];
        const tanks = Object.keys(tl.instances).map(id => {
            const name =
                getSafeInstancesProp(tcn, id, '', 'formattedValue').trim() ||
                getSafeInstancesProp(tf, id, '', 'formattedValue').trim() ||
                this.locale.getString('installationoverview.tank_unnamed');
            const fluidCode = getSafeInstancesProp(tf, id, 'unknown');
            const fillText = getSafeInstancesProp(tl, id, '0%', 'formattedValue');
            raw.push([name, parseFloat(fillText), fluidCode]);
            return `<li class="tank-${fluidCode}" >${name} - ${fillText}</li>`;
        });
        if (!tanks.length) return undefined;
        if (rawInfo) {
            return raw;
        }
        return `<ul>${tanks.join('')}</ul>`;
    }

    getTanksWithWarning(installation) {
        const tanks = this.getTanksInfo(installation, true) || [];
        return (tanks || []).filter(tank =>
            tankSortOptions.SEWAGE.includes(tank[2].toString())
                ? tank[1] >= 100 - this.lowFuelThreshold
                : tank[1] <= this.lowFuelThreshold,
        );
    }

    hasLowTanks(site) {
        const temp = this.getTanksWithWarning(site);
        return temp.length > 0;
    }

    serviceNeeded(site) {
        return propRead(site, 'gasr') === 'Warning';
    }

    serviceTime(site) {
        return propRead(site, 'gasd');
    }

    serviceInterval(site) {
        return secondsStringToHoursNumber(propRead(site, 'gasd'));
    }

    hasServiceInterval(site) {
        const interval = this.serviceInterval(site);
        return interval && interval > 0;
    }

    serviceTimeInHours(site) {
        const serviceTime = this.serviceTime(site);
        if (serviceTime) {
            return secondsStringToHoursNumber(serviceTime);
        }
        return;
    }

    isServiceTimeError(site) {
        return this.serviceTimeInHours(site) <= 0;
    }

    toggleSort(column) {
        if (column === 'battery') {
            this.batterySortDropdownOpen = !this.batterySortDropdownOpen;
        } else if (column === 'fuel') {
            this.tankSortDropdownOpen = !this.tankSortDropdownOpen;
        } else if (column === 'temperature') {
            this.temperatureSortDropdownOpen = !this.temperatureSortDropdownOpen;
        } else {
            this.sortOn(column);
        }
    }

    closeSortDropdown(e) {
        if (e.target.id !== 'battery') {
            this.batterySortDropdownOpen = false;
        }
        if (e.target.id !== 'fuel') {
            this.tankSortDropdownOpen = false;
        }
        if (e.target.id !== 'temperature') {
            this.temperatureSortDropdownOpen = false;
        }
    }

    get header() {
        return columnOrder.map(name => this.columns[name]).filter(item => item.visible);
    }

    isEnabled(name) {
        return this.enabledColumns && this.enabledColumns.indexOf(name) !== -1;
    }

    getTankValuesForSorted(site) {
        const { tl, tf } = site.data;
        const result =
            tl &&
            tl.instances &&
            Object.keys(tl.instances).map(id => ({
                id,
                fluidType: getSafeInstancesProp(tf, id, 'unknown'),
                fillPercentage: Math.round(getSafeInstancesProp(tl, id, 0)),
            }));
        return getTanksSortedByTypeAndPercentage(
            result || [],
            this.sortAttribute,
            this.sortedReverse,
        );
    }

    getTemperatureValuesForSorted(site) {
        const { tsT, tst } = site.data;
        const result =
            tsT &&
            tsT.instances &&
            Object.keys(tsT.instances).map(id => ({
                id,
                type: getSafeInstancesProp(tst, id, 'unknown'),
                temperature: Math.round(getSafeInstancesProp(tsT, id, 0)),
            }));
        return getTemperaturesSortedByTypeAndValue(
            result || [],
            this.sortAttribute,
            this.sortedReverse,
        );
    }

    tanks(site) {
        const { tr, tl, tf, tcn } = site.data;
        if (!tl) return [];
        const result = Object.keys(tl.instances).map(id => ({
            id,
            timestamp: tr && tr.timestamp,
            fluidType: getSafeInstancesProp(tf, id, 'unknown'),
            fillPercentage: Math.round(getSafeInstancesProp(tl, id, 0)),
            remainingPercentage: 100 - Math.round(getSafeInstancesProp(tl, id, 0)) + '%',
            remaining: getSafeInstancesProp(tr, id, '', 'formattedValue'),
            name:
                getSafeInstancesProp(tcn, id, '', 'formattedValue').trim() ||
                getSafeInstancesProp(tf, id, '', 'formattedValue').trim() ||
                this.locale.getString('installationoverview.tank_unnamed'),
        }));
        return this.sortedColumn === 'fuel'
            ? getTanksSortedByTypeAndPercentage(result, this.sortAttribute, this.sortedReverse)
            : result;
    }

    temperature(site) {
        const { tst, tscn, tsT } = site.data;
        if (!tsT) return [];
        const result = Object.keys(tsT.instances).map(id => {
            const typeLabel = this.locale.getString(
                `installationoverview.temperature_type_${getSafeInstancesProp(
                    tst,
                    id,
                    'unknown',
                    'textValue',
                )
                    .toString()
                    .toLowerCase()}`,
            );
            return {
                id,
                timestamp: tsT && tsT.timestamp,
                type: getSafeInstancesProp(tst, id, 'unknown'),
                typeLabel: typeLabel,
                temperature: getSafeInstancesProp(tsT, id, 0),
                temperatureFormatted: getSafeInstancesProp(tsT, id, 0, 'formattedValue'),
                scale: tsT.formatWithUnit.replace('%.1F ', ''),
                name:
                    getSafeInstancesProp(tscn, id, '', 'formattedValue').trim() ||
                    typeLabel ||
                    this.locale.getString('installationoverview.tank_unnamed'),
            };
        });
        return this.sortedColumn === 'temperature'
            ? getTemperaturesSortedByTypeAndValue(result, this.sortAttribute, this.sortedReverse)
            : result;
    }

    hasTankData(site) {
        return propValid(site, 'tl') && propRead(site, 'tl', 'instances') !== null;
    }

    hasTemperatureData(site) {
        return propValid(site, 'tsT') && propRead(site, 'tsT', 'instances') !== null;
    }

    get columns() {
        this.VrmLangService.getLang().then(language => {
            moment.locale(localeMeta.getMomentJsLocale(language));
        });

        return {
            name: {
                key: 'name',
                i18n: 'installationoverview.installationName',
                i18nFallback: 'Name',
                visible: this.isEnabled('name'),
                sortedReverse: this.sortedReverse,
                comparator: createSorter(site => site.name, this.sortedReverse),
                headerClasses: ['box--name'],
            },
            battery: {
                key: 'battery',
                i18n: 'installationoverview.stateOfCharge',
                i18nFallback: 'Battery',
                visible: this.isEnabled('stateOfCharge'),
                sortAttribute: this.sortAttribute,
                sortedReverse: this.sortedReverse,
                get comparator() {
                    return this.sortAttribute === 'bv'
                        ? createSorter(createPropReader('bv', 'rawValue'), this.sortedReverse)
                        : createSorter(createPropReader('bs', 'rawValue'), this.sortedReverse);
                },
                hasData: site => propValid(site, 'bs') || propValid(site, 'bv'),
                hasError: () => false,
                blockState: site => stateFromErrorAndData(this.columns.battery, site),
            },
            solar: {
                key: 'solar',
                i18n: 'installationoverview.solarYield',
                i18nFallback: 'Solar',
                visible: this.isEnabled('solarYield'),
                sortedReverse: this.sortedReverse,
                comparator: createSorter(
                    createPropReader('solar_yield', 'rawValue'),
                    this.sortedReverse,
                ),
                hasData: site => propValid(site, 'solar_yield'),
                hasError: () => false,
                blockState: site => stateFromErrorAndData(this.columns.solar, site),
            },
            load: {
                key: 'load',
                i18n: 'installationoverview.consumption',
                i18nFallback: 'Load',
                visible: this.isEnabled('consumption'),
                sortedReverse: this.sortedReverse,
                comparator: chainSorters([
                    createSorter(
                        site => (this.columns.load.hasError(site) ? -1 : 0),
                        this.sortedReverse,
                    ),
                    createSorter(createPropReader('consumption', 'rawValue'), this.sortedReverse),
                ]),
                hasData: site => propValid(site, 'consumption'),
                hasError: site => {
                    // In case of insufficient data, avoid false-positive
                    if (!propValid(site, 'S') || !propValid(site, 's')) {
                        return false;
                    }
                    /**
                     * State ('S')
                     *   0 = off
                     *   1 = low-power
                     *   2 = fault
                     *   3+ = operational
                     * Mode ('s')
                     *   1 = charger-only
                     *   2 = inverter-only
                     *   3 = on
                     *   4 = off
                     */
                    const state = propRead(site, 'S', 'rawValue');
                    const mode = propRead(site, 's', 'rawValue');
                    // If inverter is involved, error if state is 'off' or 'fault'
                    if ((mode === '2' || mode === '3') && (state === '0' || state === '2')) {
                        return true;
                    }
                    // No other error cases
                    return false;
                },
                blockState: site => stateFromErrorAndData(this.columns.load, site, 'ok'),
                annotation: site =>
                    this.columns.load.hasError(site)
                        ? this.locale.getString('installationoverview.load_error_inverter_off')
                        : null,
            },
            grid: {
                key: 'grid',
                i18n: 'installationoverview.grid',
                i18nFallback: 'Grid',
                visible: this.isEnabled('grid'),
                sortedReverse: this.sortedReverse,
                comparator: createSorter(
                    createPropReader('from_to_grid', 'rawValue'),
                    this.sortedReverse,
                ),
                hasData: site => propValid(site, 'from_to_grid'),
                hasError: site =>
                    propValid(site, 'Agl') && propRead(site, 'Agl', 'rawValue') === '2',
                blockState: site => stateFromErrorAndData(this.columns.grid, site, 'ok'),
                annotation: site =>
                    this.columns.grid.hasError(site)
                        ? this.locale.getString('installationoverview.grid_error_loss_of_mains')
                        : null,
            },
            paygo: {
                key: 'paygo',
                i18n: 'installationoverview.paygo',
                i18nFallback: 'PAYGo',
                visible: this.isEnabled('paygo'),
                sortedReverse: this.sortedReverse,
                comparator: createSorter(createPropReader('pgat', 'rawValue'), this.sortedReverse),
                blockState: item => ({
                    error:
                        Math.round(
                            moment
                                .duration(
                                    moment().startOf('day') -
                                        moment(propRead(item, 'pgat', 'rawValue') * 1000),
                                )
                                .asDays(),
                        ) > 0 && propRead(item, 'pgat', 'rawValue'),
                    disabled: !propRead(item, 'pgat', 'rawValue'),
                }),
                content: item => {
                    let output = '';
                    //TODO: replace from_to_grid to paygo value
                    let rawValue = propRead(item, 'pgat', 'rawValue');
                    //if value is null then no text
                    if (rawValue === null) return;

                    let expirationDate = moment(rawValue * 1000);
                    let today = moment().startOf('day');
                    let days = -Math.round(moment.duration(today - expirationDate).asDays());
                    if (days < 0) {
                        days *= -1;
                        output = 'Expired ' + days + ' days ago';
                    } else if (days >= 0 && days < 1) {
                        return expirationDate.fromNow();
                    } else {
                        output = '' + days + ' days remaining';
                    }

                    return output;
                },
            },
            serviceTime: {
                key: 'serviceTime',
                i18n: 'installationoverview.serviceTime',
                i18nFallback: 'Runtime until service',
                visible: this.isEnabled('serviceTime'),
                sortedReverse: this.sortedReverse,
                comparator: createSorter(
                    createPropReaderStringToNumber('gasd', 'rawValue'),
                    this.sortedReverse,
                ),
                hasError: site => this.isServiceTimeError(site),
                hasData: site => {
                    return !!this.serviceTimeInHours(site) && this.hasServiceInterval(site);
                },
                data: site => this.serviceTimeInHours(site),
            },
            runningSince: {
                key: 'runningSince',
                i18n: 'installationoverview.runningSince',
                i18nFallback: 'Runtime until service',
                visible: this.isEnabled('runningSince'),
                sortedReverse: this.sortedReverse,
                comparator: createSorter(
                    site =>
                        this.generatorRuntimes[site.id] &&
                        this.generatorRuntimes[site.id].data &&
                        this.generatorRuntimes[site.id].data.start,
                    this.sortedReverse,
                ),
                hasError: site => this.isServiceTimeError(site),
                hasData: site => {
                    this.ensureGeneratorRuntime(site.id);
                    return (
                        this.generatorRuntimes[site.id].data &&
                        this.generatorRuntimes[site.id].data.start
                    );
                },
                runtimeData: site => {
                    const entry = this.generatorRuntimes[site.id];

                    return entry && entry.data ? entry.data : null;
                },
                runtimeStart: site => {
                    const data = this.columns.runningSince.runtimeData(site);
                    if (!data) return;
                    const start = moment(data.start * 1000);
                    const startedToday = start.isSame(moment(), 'day');
                    return start.format(startedToday ? 'LT' : 'YYYY-MM-DD');
                },
                runtimeElapsed: site => {
                    const data = this.columns.runningSince.runtimeData(site);
                    if (!data) return;

                    const seconds = data.end - data.start;
                    if (seconds < 60) {
                        return `${seconds} sec`;
                    }
                    const minutes = Math.floor(seconds / 60);
                    if (minutes < 60) {
                        return `${minutes} min`;
                    }
                    const hours = Math.floor(minutes / 60);
                    return `${hours} hr`;
                },
            },
            generator: {
                key: 'generator',
                i18n: 'installationoverview.generator',
                i18nFallback: 'Generator',
                visible: this.isEnabled('generator'),
                sortedReverse: this.sortedReverse,
                comparator: createSorter(
                    createPropReader('generator', 'rawValue'),
                    this.sortedReverse,
                ),
                hasData: site => propValid(site, 'generator'),
                hasError: site =>
                    propValid(site, 'gaNG') && propRead(site, 'gaNG', 'rawValue') === '2',
                blockState: site => stateFromErrorAndData(this.columns.generator, site, 'ok'),
                annotation: site => {
                    if (this.columns.generator.hasError(site)) {
                        return this.locale.getString(
                            'installationoverview.generator_error_not_detected',
                        );
                    }
                    return propRead(site, 'gRC');
                },
            },
            temperature: {
                key: 'temperature',
                i18n: 'installationoverview.temperature',
                i18nFallback: 'Temperature',
                visible: this.isEnabled('temperature'),
                sortedReverse: this.sortedReverse,
                comparator: createSorterList(
                    this.getTemperatureValuesForSorted,
                    this.sortAttribute,
                    this.sortedReverse,
                    'temperature',
                    'type',
                ),
                hasData: this.hasTemperatureData,
                hasError: () => false,
                blockState: site => stateFromErrorAndData(this.columns.temperature, site, ''),
                temperatureSortOptions: temperatureSortOptions,
                sortAttribute: this.sortAttribute,
                temperature: this.temperature,
                tooltip: installation => {
                    const temperature = this.temperature(installation);

                    return temperature.length === 0
                        ? undefined
                        : `<ul>${temperature
                              .map(tank => {
                                  return `<li class="temperature-${tank.type}" >${tank.name}: ${tank.temperatureFormatted}</li>`;
                              })
                              .join('')}</ul>`;
                },
            },
            fuel: {
                key: 'fuel',
                i18n: 'installationoverview.tanks',
                i18nFallback: 'Tank',
                visible: this.isEnabled('tanks'),
                sortedReverse: this.sortedReverse,
                comparator: createSorterList(
                    this.getTankValuesForSorted,
                    this.sortAttribute,
                    this.sortedReverse,
                    'fillPercentage',
                    'fluidType',
                ),
                hasData: this.hasTankData,
                hasError: () => false,
                blockState: site => stateFromErrorAndData(this.columns.fuel, site, ''),
                tankSortOptions: tankSortOptions,
                sortAttribute: this.sortAttribute,
                tanks: this.tanks,
                tooltip: installation => {
                    const tanks = this.tanks(installation);

                    return tanks.length === 0
                        ? undefined
                        : `<ul>${tanks
                              .map(tank => {
                                  return `<li class="tank-${tank.fluidType}" >${tank.name} - ${tank.fillPercentage} %</li>`;
                              })
                              .join('')}</ul>`;
                },
            },
            lastUpdate: {
                key: 'lastUpdate',
                i18n: 'installationoverview.last_update',
                i18nFallback: 'Last update',
                visible: this.isEnabled('lastUpdate'),
                sortedReverse: this.sortedReverse,
                comparator: createSorter(site => site.last_timestamp, this.sortedReverse),
                content: site => moment(site.last_timestamp * 1000).fromNow(),
            },
            dateAdded: {
                key: 'dateAdded',
                i18n: 'installationoverview.dateAdded',
                i18nFallback: 'Date added',
                visible: this.isEnabled('dateAdded'),
                sortedReverse: this.sortedReverse,
                comparator: createSorter(site => site.syscreated, this.sortedReverse),
                content: site => moment(site.syscreated * 1000).format('YYYY-MM-DD'),
            },
            alarm: {
                key: 'alarm',
                i18n: 'installationoverview.alarm',
                i18nFallback: 'Alarm',
                visible: this.isEnabled('alarm'),
                sortedReverse: this.sortedReverse,
                comparator: chainSorters([
                    createSorter(site => (siteIsOld(site) ? 0 : -1), this.sortedReverse),
                    createSorter(site => (site.alarm ? -1 : 0), this.sortedReverse), // Alarm
                    createSorter(
                        site => (this.columns.load.hasError(site) ? -1 : 0),
                        this.sortedReverse,
                    ), // Error
                    createSorter(
                        site => (this.isServiceTimeError(site) ? -1 : 0),
                        this.sortedReverse,
                    ), // Runtime until service
                    createSorter(
                        site => (this.getTanksWithWarning(site).length > 0 ? -1 : 0),
                        this.sortedReverse,
                    ), // Tanks
                    createSorter(site => site.name, this.sortedReverse), // Name
                ]),
                siteIsOld: item => siteIsOld(item),
                blockState: item => ({
                    error: item.alarm || item.prices_unavailable,
                    warning:
                        !(item.alarm || item.prices_unavailable) && // Not error
                        (this.hasLowTanks(item) ||
                            (this.serviceNeeded(item) && this.isServiceTimeError(item))) &&
                        !this.siteIsOld(item),
                    disabled: !item.alarm && !siteIsOld(item) && !item.prices_unavailable,
                }),
                headerClasses: ['box--alarm'],
                tooltip: site => {
                    let items =
                        site.current_alarms_formatted && site.current_alarms_formatted.length > 0
                            ? site.current_alarms_formatted.map(alarmText =>
                                  generatorTooltipText(alarmText, 'red'),
                              )
                            : [];
                    if (site.prices_unavailable)
                        items.push(
                            generatorTooltipText(
                                this.$t('dashboard.prices_unavailable', {
                                    i18nFallback:
                                        'Dynamic ESS: ENTSO-E prices for your region are currently unavailable. This should soon resolve itself.',
                                }),
                                'red',
                            ),
                        );
                    const tanks = this.getTanksWithWarning(site);
                    if (tanks.length > 0 && !this.siteIsOld(site)) {
                        tanks.forEach(pair =>
                            items.push(generatorTooltipFlex(pair[0], `${pair[1]} %`, 'yellow')),
                        );
                    }

                    if (this.isServiceTimeError(site) && this.serviceNeeded(site)) {
                        const hours = this.serviceTimeInHours(site);
                        items.push(
                            generatorTooltipFlex(
                                this.$t('dashboard.generator_runtime', {
                                    i18nFallback: 'Generator runtime until service',
                                }),
                                `${hours} h`,
                                'yellow',
                            ),
                        );
                    }

                    if (items.length === 0) {
                        return undefined;
                    }
                    return `<ul>${items.join('')}</ul>`;
                },
            },
        };
    }

    sortOn(columnName, newSortAttribute, sortOrder) {
        if (newSortAttribute) {
            this.sortAttribute = newSortAttribute;
        }

        if (this.sortedColumn !== columnName) {
            this.sortedColumn = columnName;
            if (sortOrder) {
                this.sortedReverse = sortOrder === sortOrders.DESCENDING;
            } else {
                this.sortedReverse = false;
            }
        } else {
            if (sortOrder) {
                this.sortedReverse = sortOrder === sortOrders.DESCENDING;
            } else {
                this.sortedReverse = !this.sortedReverse;
            }
        }
    }

    isCurrentSort(column, value, sortOrder) {
        return (
            this.sortedReverse === (sortOrder === sortOrders.DESCENDING) &&
            this.sortedColumn === column &&
            this.sortAttribute === value
        );
    }

    sortIcon(columnName) {
        if (this.sortedColumn !== columnName) return null;
        return this.sortedReverse ? 'sort--descending' : 'sort--ascending';
    }

    get sortedItems() {
        return this.sites.slice().sort(this.columns[this.sortedColumn].comparator);
    }

    get itemHeight() {
        return this.detailMode ? 76 : 58;
    }

    get sites() {
        const sites = this.installations || [];
        if (this.search) {
            return this.$filter('installationOverviewFilter')(sites, this.search, undefined);
        } else {
            return sites;
        }
    }

    created() {
        this.onMouseMoveThrottled = debounce(e => {
            this.onMouseEnter(e);
        }, 5);

        if (Capabilities.hasTouchSupport()) {
            document.body.addEventListener('click', e => {
                this.onMinigraphUntap(e, this.storedId);
            });
        }
    }

    mounted() {
        this.hasMounted = true;
        this.bufferSize = window.innerHeight;
        const scrollArea = this.$el.getElementsByClassName('vrm-scroll-fade__scrollarea')[0];
        const scrollHandler = this.onScroll.bind(this);
        this.unbindScrollHandler = () => {
            scrollArea.removeEventListener('scroll', scrollHandler);
            window.removeEventListener('scroll', scrollHandler);
            window.removeEventListener('resize', scrollHandler);
        };
        scrollArea.addEventListener('scroll', scrollHandler);
        window.addEventListener('scroll', scrollHandler);
        window.addEventListener('resize', scrollHandler);
        window.addEventListener('click', this.closeSortDropdown);
        // Apply initial layout
        this.onScroll();
    }

    @Watch('enabledColumns')
    onEnabledColumnsChanged() {
        for (let key in this.charts) {
            let rowCharts = this.charts[key];

            for (let attr in rowCharts) {
                let chart = rowCharts[attr];

                // Enabled columns uses different attribute naming
                // Map column naming to chart/data naming and check
                // if an existing chart is still enabled.
                if (chart) {
                    const enabled = this.enabledColumns.includes(
                        this.mapAttributeToEnabledColumnId(attr),
                    );

                    if (!enabled) {
                        // Remove stored chart values if the column is disabled.
                        this.charts[key][attr] = undefined;
                    }
                }
            }
        }
    }

    /**
     * Column identifiers for this.enabledColumns are different from data attribute naming used in charts.
     *
     * Given a data attribute, map it to its column naming.
     *
     * @param attr
     */
    mapAttributeToEnabledColumnId(attr) {
        switch (attr) {
            case 'bs':
                return 'stateOfCharge';
            case 'from_to_grid':
                return 'grid';
            case 'solar_yield':
                return 'solarYield';
            default:
                return attr;
        }
    }

    onGraphReady(e) {
        if (!(e.idSite in this.charts)) {
            this.charts[e.idSite] = {};
        }

        this.charts[e.idSite][e.attribute] = e.chart;
        this.highlightedPoints[e.idSite] = {};
    }

    /**
     * Create event listeners to be able to intercept clicks/taps on graph cells and prevent the router from
     * navigating.
     *
     * @param item - The installation item
     * @param attribute - The data attribute name
     */
    getRouterInterceptorsForTouch(item, attribute, state) {
        let listeners = {};

        if (Capabilities.hasTouchSupport()) {
            listeners['click'] = e => {
                e.preventDefault();
                e.stopPropagation();

                if (!state.disabled) {
                    // Not a disabled column.
                    this.$refs[`chart-${attribute}-${item.id}`].onTap(e);
                }
            };
        }

        return listeners;
    }

    /**
     * Called whenever the minigraph is tapped on a touch device.
     *
     * @param e
     */
    onMinigraphTap(e) {
        // // Prevent navigation.
        e.event.preventDefault();
        e.event.stopPropagation();

        const idSite = e.idSite;
        const attribute = e.attribute;

        const charts = this.charts[idSite] || {};

        const srcChartPoint = e.point;
        const srcTimestamp = e.timestamp;

        if (!srcChartPoint || !srcTimestamp) {
            console.warn(idSite, attribute, 'No src point or timestamp found.');
            return;
        }

        if (this.storedId !== idSite) {
            if (this.storedId) {
                this.onMinigraphUntap(e.event, this.storedId);
            }

            this.storedId = idSite;
        }

        this.tooltipTimestamp = moment(srcTimestamp).format('YYYY[-]MM[-]DD');
        this.tooltipTimestampHours = moment(srcTimestamp).format('HH:mm');

        for (let key in charts) {
            const chart = charts[key];
            if (!chart) continue;

            let point;
            //Stategraph's points need to be found in a different way than the normal graphs.
            if (chart.series[0] && chart.series[0].type === 'columnrange') {
                point = this.searchPointColumnRange(srcChartPoint, srcTimestamp, chart);
            } else {
                point = this.searchPoint(srcTimestamp, chart);
            }

            // If there is a previously stored point that was hovered, update it to disable the marker again.
            // Prevents marker circles from remaining on the chart after hovering in specific situations.
            const lastPoint = this.highlightedPoints[idSite][key];
            if (lastPoint) {
                lastPoint.setState('normal');
                lastPoint.state = '';
                lastPoint.update({
                    marker: {
                        enabled: false,
                    },
                });

                if (lastPoint.onMouseOut) lastPoint.onMouseOut();
                if (lastPoint.series && lastPoint.series.onMouseOut) lastPoint.series.onMouseOut();
            }

            if (point) {
                if (!this.storedId) {
                    this.storedId = idSite;
                }

                if (point.series.onMouseOver) {
                    point.series.onMouseOver(e);
                }

                point.setState('hover');
                point.state = '';

                this.highlightedPoints[idSite][key] = point;

                this.updateTooltip(key, point.y);
            } else if (!point) {
                chart.tooltip.hide();
                chart.xAxis[0].hideCrosshair();
                chart.yAxis[0].hideCrosshair();
            }
        }
    }

    onMinigraphUntap(e, idSite) {
        this.tooltipformatBattery = null;
        this.tooltipformatSolar = null;
        this.tooltipformatLoad = null;
        this.tooltipformatGrid = null;
        this.tooltipformatGenerator = null;
        this.storedId = null;

        const charts = this.charts[idSite] || {};

        let service = this.GraphService;
        if (!service.highcharts) return;

        for (let key in charts) {
            const chart = charts[key];
            const point = this.highlightedPoints[idSite][key];

            if (!chart) continue;

            if (point) {
                point.setState('normal');
                point.update({
                    marker: {
                        enabled: false,
                    },
                });

                point.state = '';

                if (point.onMouseOut) point.onMouseOut();
                if (point.series && point.series.onMouseOut) point.series.onMouseOut();
            }

            chart.tooltip.refresh();
            chart.tooltip.hide();
            chart.xAxis[0].hideCrosshair();
            chart.yAxis[0].hideCrosshair();
            chart.redraw();
        }

        this.highlightedPoints[idSite] = {};
    }

    onMouseEnter(e) {
        // Highlight points on all charts for this idsite
        const idSite = e.idSite;
        const attr = e.attribute;
        const srcChartPoint = e.point;
        const srcTimestamp = e.timestamp;

        if (!srcChartPoint || !srcTimestamp) {
            return;
        }

        if (this.storedId !== idSite) {
            this.storedId = idSite;
        }

        // store id in prp
        const charts = this.charts[idSite] || {};

        this.tooltipTimestamp = moment(srcTimestamp).format('YYYY[-]MM[-]DD');
        this.tooltipTimestampHours = moment(srcTimestamp).format('HH:mm');

        for (let key in charts) {
            const chart = charts[key];
            if (!chart) continue;

            let point;
            //Stategraph's points need to be found in a different way than the normal graphs.
            if (chart.series[0] && chart.series[0].type === 'columnrange') {
                point = this.searchPointColumnRange(srcChartPoint, srcTimestamp, chart);
            } else {
                point = this.searchPoint(srcTimestamp, chart);
            }

            // If there is a previously stored point that was hovered, update it to disable the marker again.
            // Prevents marker circles from remaining on the chart after hovering in specific situations.
            const lastPoint = this.highlightedPoints[idSite][key];
            if (lastPoint) {
                lastPoint.setState('normal');
                lastPoint.state = '';
                lastPoint.update({
                    marker: {
                        enabled: false,
                    },
                });

                if (lastPoint.onMouseOut) lastPoint.onMouseOut();
                if (lastPoint.series && lastPoint.series.onMouseOut) lastPoint.series.onMouseOut();
            }

            if (point && point.highlight) {
                if (!this.storedId) {
                    this.storedId = idSite;
                }

                if (point.series.onMouseOver) {
                    if (attr !== key) point.series.onMouseOver(e);
                }

                point.setState('hover');
                point.state = '';

                this.highlightedPoints[idSite][key] = point;

                this.updateTooltip(key, point.y);
            } else if (!point) {
                chart.tooltip.hide();
                chart.xAxis[0].hideCrosshair();
                chart.yAxis[0].hideCrosshair();
            }
        }
    }

    onMouseLeave(e) {
        this.onMouseMoveThrottled.cancel();
        this.onMinigraphUntap(e, this.storedId);
    }

    updateTooltip(key, value) {
        switch (key) {
            case 'bs':
                this.tooltipformatBattery = value.toFixed(0) + ' %';
                break;
            case 'solar_yield':
                this.tooltipformatSolar = value.toFixed(0) + ' W';
                break;
            case 'consumption':
                this.tooltipformatLoad = value.toFixed(0) + ' W';
                break;
            case 'from_to_grid':
                this.tooltipformatGrid = value.toFixed(0) + ' W';
                break;
            case 'generator':
                this.tooltipformatGenerator = value.toFixed(0) + ' W';
                break;
        }
    }

    searchPoint(x, chart) {
        if (!chart || !chart.series.length) return;
        const firstVisibleSeries = _.findIndex(chart.series, 'visible');
        if (firstVisibleSeries < 0) return;
        const points = chart.series[firstVisibleSeries].points;
        if (!points) return;
        return points[_.sortedIndexBy(points, { x }, 'x')];
    }

    searchPointColumnRange(scrPoint, y, chart) {
        if (!chart || !chart.series.length) return;
        const firstVisibleSeries = _.findIndex(chart.series, 'visible');
        if (firstVisibleSeries < 0) return;
        const points = chart.series[firstVisibleSeries].points;
        if (!points) return;
        let returnPoint;
        let possiblePoints = [];
        //check all bars that cover the mouse location
        chart.series.forEach(serie => {
            serie.data.forEach(point => {
                if (point.low < y && point.high >= y) {
                    possiblePoints.push(point);
                    if (scrPoint && point.x === scrPoint.x) {
                        returnPoint = point;
                    }
                }
            });
        });
        //if specific bar is hovered over return this.
        if (returnPoint) return returnPoint;

        //If no bars are found check if there is one within 1 minute (
        //so that states of seconds will still be shown)
        const oneMinute = 60000;
        if (possiblePoints.length === 0) {
            chart.series.forEach(serie => {
                serie.data.forEach(point => {
                    if (point.low - oneMinute < y && point.high + oneMinute >= y) {
                        possiblePoints.push(point);
                    }
                });
            });
        }

        //return the closest/tightest horizontal bar to the mouse cursor given the
        let minimalDistance = Number.MAX_VALUE;
        possiblePoints.forEach(point => {
            const currentDistance = Math.abs(y - point.low) + Math.abs(point.high - y);
            if (currentDistance < minimalDistance) {
                minimalDistance = currentDistance;
                returnPoint = point;
            }
        });
        return returnPoint;
    }

    beforeDestroy() {
        if (this.unbindScrollHandler) {
            this.unbindScrollHandler();
            this.unbindScrollHandler = null;
        }
        document.removeEventListener('click', this.closeSortDropdown);
    }

    pageTop = 0;

    mainBounds = {
        left: 0,
        width: 0,
        top: 0,
        height: 0,
    };

    scrollerBounds = {
        left: 0,
        width: 0,
        top: 0,
        height: 0,
    };

    onVerticalScroll() {
        /** @type {HTMLElement} */
        const { left, top, width, height } = this.$el.getBoundingClientRect();
        if (left !== this.scrollerBounds.left) {
            this.mainBounds.left = left;
        }
        if (top !== this.scrollerBounds.top) {
            this.mainBounds.top = top;
        }
        if (width !== this.scrollerBounds.width) {
            this.mainBounds.width = width;
        }
        if (height !== this.scrollerBounds.height) {
            this.mainBounds.height = height;
        }
    }

    onHorizontalScroll() {
        const { left, top, width, height } = this.$refs.scroller.$el.getBoundingClientRect();
        if (left !== this.scrollerBounds.left) {
            this.scrollerBounds.left = left;
        }
        if (top !== this.scrollerBounds.top) {
            this.scrollerBounds.top = top;
        }
        if (width !== this.scrollerBounds.width) {
            this.scrollerBounds.width = width;
        }
        if (height !== this.scrollerBounds.height) {
            this.scrollerBounds.height = height;
        }
    }

    debounced = false;
    onScroll() {
        if (!this.debounced) {
            this.debounced = true;
            this.$nextTick(() => {
                this.onVerticalScroll();
                this.onHorizontalScroll();
                this.pageTop = window.innerWidth < 1280 ? 40 : 0;
                this.debounced = false;
            });
        }
    }

    get isHeaderFollowing() {
        return this.mainBounds.top < this.pageTop;
    }

    get headerStyle() {
        if (!this.isHeaderFollowing) return null;
        const boundsShift = this.mainBounds.left - this.scrollerBounds.left;
        return {
            position: 'fixed',
            top: `${this.pageTop}px`,
            left: `calc(${this.scrollerBounds.left}px - 1rem)`,
            width: `calc(${this.scrollerBounds.width}px + 2rem)`,
            paddingLeft: '1rem',
            paddingRight: '1rem',
            borderBottom: `1px solid ${
                this.AppearanceService.isDarkModeActive() ? '#3c3c3c' : '#dcdbd7'
            }`,
            zIndex: 6,
            backgroundColor: this.AppearanceService.isDarkModeActive() ? '#2e2e2d' : '#f0efeb',
            // Use clip-path to hide the left side overflow
            clipPath: `inset(0 ${-boundsShift}px 0 ${boundsShift}px)`,
        };
    }

    get headerCellStyle() {
        if (!this.isHeaderFollowing) return null;
        return {
            border: 'none',
        };
    }

    get tableStyle() {
        if (!this.isHeaderFollowing) return null;
        return {
            paddingTop: '32px',
        };
    }

    alarmTotal() {
        return `${this.sortedItems.filter(item => item.alarm && !siteIsOld(item)).length}/${
            this.sortedItems.length
        }`;
    }

    propTotal(column) {
        let sum = 0;
        this.sortedItems.forEach(
            item => (sum += Math.round(propRead(item, this.columnsWithEnergy[column], 'rawValue'))),
        );
        return sum + ' W';
    }

    /**
     * @type {Object.<string,{promise: Promise, data: (null|{live:boolean,start:number,end:number})}>}
     */
    generatorRuntimes = {};
    generatorRuntimeQueue = [];
    generatorRuntimeThrottle = null;
    generatorRuntimeThrottleTimeout = 1000;

    ensureGeneratorRuntime(idSite) {
        const isPresent = idSite in this.generatorRuntimes;
        if (!isPresent) {
            Vue.set(this.generatorRuntimes, idSite, { data: null });
            this.queueGeneratorRuntime(idSite);
        }
    }

    queueGeneratorRuntime(idSite) {
        const isQueued = idSite in this.generatorRuntimeQueue;
        if (!isQueued) {
            this.generatorRuntimeQueue.push(idSite);
            if (!this.generatorRuntimeThrottle) {
                this.generatorRuntimeThrottle = setTimeout(
                    this.loadGeneratorRuntimes.bind(this),
                    this.generatorRuntimeThrottleTimeout,
                );
            }
        }
    }

    async loadGeneratorRuntimes() {
        this.generatorRuntimeThrottle = null;
        const idSites = this.generatorRuntimeQueue;
        this.generatorRuntimeQueue = [];
        const response = await this.ApiService.call2(`/installations/generator-runtimes`, {
            idSites,
        });
        this.$emit('running-since-loaded', response.data);
        idSites.forEach(idSite => {
            if (this.generatorRuntimes[idSite]) {
                this.generatorRuntimes[idSite].data = response.data[idSite];
            }
        });
    }

    @Watch('installations', { immediate: true })
    cleanupGeneratorRuntimes() {
        const sites = new Set(this.installations.map(site => `${site.id}`));
        const downloaded = Object.keys(this.generatorRuntimes);
        // Remove downloaded information for sites that are no longer present in the active installation list
        downloaded.forEach(idSite => {
            if (!sites.has(idSite)) {
                Vue.delete(this.generatorRuntimes, idSite);
            } else {
                this.queueGeneratorRuntime(parseInt(idSite));
            }
        });
    }

    tooltipColumns = ['name', 'battery', 'solar', 'load', 'grid', 'generator'];

    get activeColumnsWithoutChart() {
        let activeColumns = [];

        for (let index in this.columns) {
            // Find all visible columns that don't have a chart
            if (
                this.columns[index] &&
                this.columns[index].visible &&
                !this.tooltipColumns.includes(index)
            ) {
                activeColumns.push(index);
            }
        }

        return activeColumns;
    }

    get tooltipFlex() {
        let flex = 0;

        for (let index in this.tooltipColumns) {
            const key = this.tooltipColumns[index];

            // Find all visible columns that don't have a chart
            if (this.columns[key] && this.columns[key].visible) {
                flex += 1;
            }
        }

        return flex;
    }

    get tooltipSpacerFlex() {
        const columns = this.activeColumnsWithoutChart;
        let count = columns.length;

        if (columns.includes('alarm')) {
            count -= 1;
        }

        return count;
    }
}
</script>
